/**
 * O que o painel guarda no aparelho (onda 318, spec 06b §5.3, M1 e M2).
 *
 * ── Duas memórias, as duas de conforto ────────────────────────────────
 *
 *  · M2 · «+N desde a sua última visita». Ao abrir, o painel lê os números
 *    que mostrou na visita anterior e grava os de agora. O cartão cujo
 *    número subiu diz quanto. A chave é a da spec,
 *    `labmanager.painel.visto.<usuário>`: por usuário, porque o aparelho do
 *    laboratório passa de mão em mão, e o «novo» de um não é o do outro.
 *  · M1 · «Tudo | Comigo». A escolha da pessoa volta na visita seguinte
 *    (`labmanager.painel.comigo.<usuário>`).
 *
 * As duas moram no `localStorage` e são ENFEITE: a janela privada, o site
 * com os dados bloqueados e a cota cheia fazem o armazenamento lançar
 * exceção ao ser lido ou gravado — ou nem existir. Por isso toda leitura e
 * toda gravação ficam dentro de um `try`, e a falta delas tem um sentido
 * só: nenhuma visita anterior (nada aparece como novo) e «Tudo». A tela
 * nunca quebra por causa do aparelho (ponto de revisão 3).
 *
 * ── Por que a visita tem uma marca (`@visita`) ────────────────────────
 *
 * Cartão com zero não existe, e por isso não se guarda: a chave ausente
 * quer dizer zero. Mas «nada guardado» também é o que a primeira visita
 * (ou o aparelho que não guarda) lê — e aí TODO cartão pareceria novo. A
 * marca `@visita` (o instante da visita) separa as duas coisas: sem ela,
 * não houve visita anterior e nada é novo; com ela, o cartão que não estava
 * lá apareceu desde então, e conta inteiro. Nenhuma chave de cartão começa
 * com «@».
 *
 * O espaço (`espaco`) é obrigatório e vem de quem monta o painel — o do
 * LabManager passa o dele, e o do Campo passará o seu: o molde não sabe de
 * área nenhuma, nem tem uma «de sempre» (revisão do ramo). Cada espaço tem
 * as suas chaves, e um painel não lê a base do outro.
 */

/** A marca da visita anterior (e quando foi, em milissegundos). */
export const VISITA = '@visita';

const chaveDoVisto = (usuario: string, espaco: string) => `${espaco}.painel.visto.${usuario}`;
const chaveDoComigo = (usuario: string, espaco: string) => `${espaco}.painel.comigo.${usuario}`;

/** O armazenamento do aparelho — ou nada, quando o navegador o recusa. */
function armazenamento(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Os números que o painel mostrou na visita anterior, por chave de cartão,
 * com a marca `@visita`. `{}` quando não houve visita (ou o aparelho não
 * guarda, ou o que estava guardado não se lê).
 */
export function lerVisto(usuario: string, espaco: string): Record<string, number> {
  if (!usuario) return {};
  try {
    const cru = armazenamento()?.getItem(chaveDoVisto(usuario, espaco));
    if (!cru) return {};
    const lido: unknown = JSON.parse(cru);
    if (!lido || typeof lido !== 'object' || Array.isArray(lido)) return {};
    const saida: Record<string, number> = {};
    for (const [k, v] of Object.entries(lido as Record<string, unknown>)) {
      if (typeof v === 'number' && Number.isFinite(v)) saida[k] = v;
    }
    return VISITA in saida ? saida : {};
  } catch {
    return {};
  }
}

/** Grava os números que o painel mostra agora (só os maiores que zero). */
export function gravarVisto(usuario: string, numeros: Record<string, number>, espaco: string): void {
  if (!usuario) return;
  try {
    const guardar: Record<string, number> = { [VISITA]: Date.now() };
    for (const [k, v] of Object.entries(numeros)) {
      if (!k.startsWith('@') && Number.isFinite(v) && v > 0) guardar[k] = v;
    }
    armazenamento()?.setItem(chaveDoVisto(usuario, espaco), JSON.stringify(guardar));
  } catch {
    /* conforto: sem gravar, a próxima visita só não sabe o que é novo */
  }
}

/**
 * Quanto cada cartão subiu desde a visita anterior: a diferença, ou o
 * número inteiro do cartão que não existia. Igual ou menor não é novo. Sem
 * visita anterior (sem a marca), nada é novo.
 */
export function subiuDesde(
  anterior: Record<string, number>, agora: Record<string, number>,
): Record<string, number> {
  if (!(VISITA in anterior)) return {};
  const novos: Record<string, number> = {};
  for (const [k, v] of Object.entries(agora)) {
    if (k.startsWith('@') || !Number.isFinite(v)) continue;
    const antes = anterior[k] ?? 0;
    if (v > antes) novos[k] = v - antes;
  }
  return novos;
}

/** Se a pessoa escolheu «Comigo» da última vez. Sem aparelho, «Tudo». */
export function lerComigo(usuario: string, espaco: string): boolean {
  if (!usuario) return false;
  try {
    return armazenamento()?.getItem(chaveDoComigo(usuario, espaco)) === 'comigo';
  } catch {
    return false;
  }
}

/** Guarda a escolha; «Tudo», o padrão, não ocupa o aparelho. */
export function gravarComigo(usuario: string, comigo: boolean, espaco: string): void {
  if (!usuario) return;
  try {
    const a = armazenamento();
    if (!a) return;
    if (comigo) a.setItem(chaveDoComigo(usuario, espaco), 'comigo');
    else a.removeItem(chaveDoComigo(usuario, espaco));
  } catch {
    /* conforto: a escolha vale na visita, só não volta na próxima */
  }
}
